import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftAddon,
  Link,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  VStack,
  IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon, DeleteIcon } from '@chakra-ui/icons';
import { MdInventory2, MdLinkOff, MdStorefront } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import { getStoresListApi } from 'features/stores/stores.api';
import { getSuppliersListApi } from 'features/suppliers/suppliers.api';
import {
  getSkuItemsOverviewApi,
  getSkuItemsPageApi,
  resyncSkuItemListingsApi,
} from 'features/sku-management/sku-management.api';
import { clearSkuManagementError } from 'features/sku-management/sku-management.slice';

import { useColors } from './colors';
import ItemsCatalogAttributeModals from './ItemsCatalogAttributeModals';
import CopyFromListingModal from './CopyFromListingModal';
import EditableSupplierShippingCell from './EditableSupplierShippingCell';
import ItemsPagination from './ItemsPagination';
import SkuOptionsMenu from './SkuOptionsMenu';
import DeleteListingModal from './DeleteListingModal';

import { formatDateTime } from 'utils/date.util';

const fmtDate = (d) => formatDateTime(d) || '—';

const profileLevelLabel = (level) => level || '—';

const profileBadge = (p) => {
  if (!p || p === 'NotCalculated') {
    return { colorScheme: 'gray', label: 'Not calculated' };
  }
  if (p === 'Complete') {
    return { colorScheme: 'green', label: 'Complete' };
  }
  if (p === 'Partial') {
    return { colorScheme: 'yellow', label: 'Partial' };
  }
  if (p === 'Incomplete') {
    return { colorScheme: 'orange', label: 'Incomplete' };
  }
  return { colorScheme: 'gray', label: String(p) };
};

const optimizerStatusBadge = (tracking) => {
  const status = tracking?.status ?? 'not-optimized';
  if (status === 'optimized') {
    return { colorScheme: 'teal', label: 'Optimized' };
  }
  if (status === 'partial') {
    return { colorScheme: 'yellow', label: 'Partially optimized' };
  }
  if (status === 'halted') {
    const runs = tracking?.runCount ?? 3;
    return {
      colorScheme: 'purple',
      label: 'Optimizer halted',
      tooltip: `Excluded from future optimizer runs after ${runs} attempt${runs === 1 ? '' : 's'} without full optimization.`,
    };
  }
  if (status === 'error') {
    return { colorScheme: 'red', label: 'Optimizer error' };
  }
  return null;
};

const OptimizerVerifiedBadge = () => (
  <Badge colorScheme="teal" variant="subtle" fontSize="2xs" px={1.5} py={0.5} flexShrink={0}>
    Verified
  </Badge>
);

const AttributeCountRow = ({ label, count, verified, textColor }) => (
  <HStack spacing={2} align="center">
    <Text fontSize="sm" color={textColor}>
      {label} {count}
    </Text>
    {verified ? <OptimizerVerifiedBadge /> : null}
  </HStack>
);

const dataProfileTooltip = (row) => {
  if (row.dataProfile === 'NotCalculated' || !row.dataProfileBreakdown) {
    return 'Data profile has not been calculated yet. Run recalculation from SKU settings.';
  }
  const attrs = row.attributes || {};
  const b = row.dataProfileBreakdown;
  const lines = [
    `Fitment: ${attrs.fitmentCount ?? 0}${b?.fitment ? ` (${profileLevelLabel(b.fitment)})` : ''}`,
    `Specs: ${attrs.specsCount ?? 0}${b?.specifications ? ` (${profileLevelLabel(b.specifications)})` : ''}`,
    `Images: ${attrs.imageCount ?? 0}${b?.images ? ` (${profileLevelLabel(b.images)})` : ''}`,
  ];
  return lines.join(' · ');
};

const isInStockLabel = (label) =>
  typeof label === 'string' && label.toLowerCase().includes('in stock');

/** Same rules as listings DataTable: live marketplace listing from external id. */
const marketplaceListingUrl = (marketplaceName, externalPlatformId) => {
  const m = (marketplaceName || '').toLowerCase();
  const id = (externalPlatformId || '').trim();
  if (!id) return null;
  if (m === 'ebay') return `https://www.ebay.com/itm/${id}`;
  if (m === 'amazon') return `https://www.amazon.com/dp/${id}`;
  return null;
};

/** Store listing sub-table: tighter balance so the listing column does not float away from the rest */
const STORE_GRID_COLS = {
  base: '1fr',
  lg: 'minmax(0, 2.4fr) minmax(0, 1fr) minmax(0, 0.62fr) minmax(0, 0.82fr) minmax(0, 0.48fr) minmax(0, 0.72fr) minmax(0, 0.88fr) minmax(0, 0.4fr)',
};

const ORDER_SUPPLIER_GRID_COLS = {
  base: '1fr',
  lg: 'minmax(0, 1.2fr) repeat(4, minmax(0, 0.7fr)) repeat(3, minmax(0, 0.55fr))',
};

const ORDER_RECENT_GRID_COLS = {
  base: '1fr',
  lg: 'minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 0.9fr) minmax(0, 0.8fr) minmax(0, 0.45fr) minmax(0, 0.9fr) minmax(0, 1.1fr)',
};

const emptyOrderStats = () => ({
  orderCount: 0,
  unitsOrdered: 0,
  cancellationCount: 0,
  unitsCancelled: 0,
  returnCount: 0,
  unitsReturned: 0,
  partialRefundCount: 0,
  unitsPartiallyRefunded: 0,
  topSupplierByOrders: null,
  topReturnSupplier: null,
  supplierBreakdown: [],
  marketplaceBreakdown: [],
  recentActivity: [],
});

/** Matches listing/qty badges in the SKU column (px={3}, py={1}, count + label with gap). */
const StatCountBadge = ({ colorScheme, count, label }) => (
  <Badge
    colorScheme={colorScheme}
    variant="subtle"
    px={3}
    py={1}
    borderRadius="md"
    fontWeight="medium"
    textTransform="none"
  >
    <HStack as="span" spacing={2}>
      <Text as="span">{count}</Text>
      <Text as="span">{label}</Text>
    </HStack>
  </Badge>
);

const StatusLabelBadge = ({ colorScheme, children }) => (
  <Badge
    colorScheme={colorScheme}
    variant="subtle"
    px={3}
    py={1}
    borderRadius="md"
    fontWeight="medium"
    textTransform="none"
  >
    {children}
  </Badge>
);

const OrderLineStatusBadges = ({ activity }) => {
  if (activity.isCancelled) {
    return <StatusLabelBadge colorScheme="orange">Cancelled</StatusLabelBadge>;
  }
  if (activity.isReturn) {
    return <StatusLabelBadge colorScheme="red">Returned / refunded</StatusLabelBadge>;
  }
  if (activity.isPartialRefund) {
    return <StatusLabelBadge colorScheme="yellow">Partially refunded</StatusLabelBadge>;
  }
  return (
    <StatusLabelBadge colorScheme="green">{activity.statusLabel || 'Fulfilled'}</StatusLabelBadge>
  );
};

function marketplaceSyncLabel(sync) {
  if (!sync?.status) {
    return null;
  }
  if (sync.status === 'success') {
    return 'Synced';
  }
  if (sync.status === 'error') {
    return 'Error';
  }
  if (sync.status === 'skipped') {
    return 'Skipped';
  }
  return sync.status;
}

function marketplaceSyncColorScheme(sync) {
  if (!sync?.status) {
    return 'gray';
  }
  if (sync.status === 'success') {
    return 'green';
  }
  if (sync.status === 'error') {
    return 'red';
  }
  return 'gray';
}

const supplierRowsFromItem = (row) => {
  if (Array.isArray(row.suppliers) && row.suppliers.length > 0) {
    return row.suppliers;
  }
  return (row.supplierNames || []).map((name) => ({
    supplierId: '',
    name,
    supplierIdentifier: '',
    partslink: '',
    oem: '',
    inventoryIncludesShipping: true,
    externalShippingCost: null,
    inventoryMatch: null,
  }));
};

/** Sum inventory `quantity` across supplier rows with a matched inventory line. */
function sumSupplierInventoryQuantities(supplierList) {
  if (!Array.isArray(supplierList) || supplierList.length === 0) {
    return null;
  }
  let sum = 0;
  let any = false;
  for (const s of supplierList) {
    const q = s?.inventoryMatch?.quantity;
    if (typeof q === 'number' && Number.isFinite(q)) {
      sum += q;
      any = true;
    }
  }
  return any ? sum : null;
}

/**
 * Mean listed marketplace price (`price`) for listings that also have a numeric supplier
 * selling price (so both sides of the sell vs list relationship exist).
 */
function avgListedPriceWhereSellingPriceExists(listingRows) {
  if (!Array.isArray(listingRows) || listingRows.length === 0) {
    return null;
  }
  const values = [];
  for (const lr of listingRows) {
    const listed = Number(lr.price);
    const sellingRaw = lr.supplier?.sellingPrice;
    const selling =
      sellingRaw != null && sellingRaw !== '' ? Number(sellingRaw) : Number.NaN;
    if (!Number.isFinite(listed) || listed <= 0) {
      continue;
    }
    if (!Number.isFinite(selling)) {
      continue;
    }
    values.push(listed);
  }
  if (values.length === 0) {
    return null;
  }
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/** Expanded supplier sources table columns */
const SUPPLIER_SOURCES_GRID_COLS =
  'minmax(100px, 1.05fr) minmax(96px, 1fr) minmax(96px, 1fr) minmax(80px, 0.9fr) minmax(80px, 0.9fr) minmax(64px, 0.65fr) minmax(88px, 0.8fr) minmax(80px, 0.75fr)';

const dashCell = (v) => {
  if (v == null || v === '') return '—';
  const t = String(v).trim();
  if (!t || t.toLowerCase() === 'undefined') return '—';
  return t;
};

const formatInventoryShipping = (prices, inventoryIncludesShipping, externalShippingCost) => {
  const sumFromPrices = () => {
    if (!prices || typeof prices !== 'object' || Array.isArray(prices)) return null;
    const sc = Number(prices.shippingCost);
    const hc = Number(prices.handlingCost);
    const hasSc = Number.isFinite(sc);
    const hasHc = Number.isFinite(hc);
    if (!hasSc && !hasHc) return null;
    return (hasSc ? sc : 0) + (hasHc ? hc : 0);
  };

  if (inventoryIncludesShipping === false) {
    if (externalShippingCost != null && Number.isFinite(Number(externalShippingCost))) {
      return `$${Number(externalShippingCost).toFixed(2)}`;
    }
    const invSum = sumFromPrices();
    return invSum != null ? `$${invSum.toFixed(2)}` : '—';
  }

  const invSum = sumFromPrices();
  return invSum != null ? `$${invSum.toFixed(2)}` : '—';
};

const SupplierInventoryPricesTooltip = ({ prices }) => {
  const { textColor, mutedTextColor } = useColors();
  const entries =
    prices && typeof prices === 'object' && !Array.isArray(prices)
      ? Object.entries(prices)
      : [];
  if (entries.length === 0) {
    return (
      <Text fontSize="sm" color={mutedTextColor}>
        No price fields
      </Text>
    );
  }
  return (
    <VStack align="stretch" spacing={1} maxW="300px">
      {entries.map(([k, v]) => (
        <Text key={k} fontSize="xs" color={textColor}>
          <Text as="span" fontWeight="semibold">
            {k}:
          </Text>{' '}
          {typeof v === 'number' && Number.isFinite(v) ? v : String(v)}
        </Text>
      ))}
    </VStack>
  );
};

/** Read-only field row (listing SupplierInfo / SupplierSettings style). */
const TooltipReadonlyField = ({ label, prefix, value }) => {
  const { mutedTextColor, textColor, tooltipCodeBg, borderColor } = useColors();
  return (
    <FormControl size="xs">
      <FormLabel fontSize="xs" fontWeight="semibold" color={mutedTextColor} mb={1}>
        {label}
      </FormLabel>
      <InputGroup size="xs">
        <InputLeftAddon
          px={2}
          fontSize="xs"
          bg={tooltipCodeBg}
          borderColor={borderColor}
          children={prefix}
        />
        <Input
          value={value}
          isReadOnly
          size="xs"
          fontSize="xs"
          bg={tooltipCodeBg}
          color={textColor}
          borderColor={borderColor}
        />
      </InputGroup>
    </FormControl>
  );
};

const fmtPct = (decimal) =>
  decimal != null && !Number.isNaN(Number(decimal))
    ? (Number(decimal) * 100).toFixed(1)
    : null;

const ListingPriceTooltipLabel = ({ lr }) => {
  const {
    textColor,
    mutedTextColor,
    borderColor,
    tooltipHeading,
    tooltipCodeBg,
  } = useColors();
  const sup = lr.supplier;
  const opt = lr.optimization;
  const cfg = lr.storeSupplierConfig;
  const pricesObj =
    sup?.prices?.length > 0
      ? Object.fromEntries(sup.prices.map((p) => [p.key, p.value]))
      : null;
  const markupStr = fmtPct(cfg?.markupPercentage);
  const tolStr = fmtPct(cfg?.priceTolerancePercentage);

  return (
    <VStack align="stretch" spacing={0} maxW="380px">
      <Box>
        <Text fontWeight="semibold" fontSize="sm" color={tooltipHeading} mb={1}>
          Listing price
        </Text>
        <Text fontSize="sm" color={textColor} lineHeight="1.5">
          ${Number(lr.price || 0).toFixed(2)}{' '}
          <Text as="span" color={mutedTextColor} fontSize="xs">
            (details.price)
          </Text>
        </Text>
      </Box>

      <Divider borderColor={borderColor} my={2.5} />

      {sup ? (
        <>
          <Text fontWeight="semibold" fontSize="sm" color={tooltipHeading} mb={2}>
            Supplier pricing
          </Text>
          <Text fontSize="xs" color={mutedTextColor} mb={0.5}>
            Supplier
          </Text>
          <Text fontSize="sm" color={textColor} mb={1}>
            {sup.name || '—'}
          </Text>
          <Text fontSize="xs" color={mutedTextColor} mb={0.5}>
            Supplier ID
          </Text>
          <Text fontSize="sm" color={textColor} mb={2}>
            {sup.supplierIdentifier || '—'}
          </Text>
          <HStack align="stretch" spacing={3} mb={2}>
            <TooltipReadonlyField label="Total cost" prefix="$" value={Number(sup.cost || 0).toFixed(2)} />
            <TooltipReadonlyField
              label="Selling price"
              prefix="$"
              value={Number(sup.sellingPrice || 0).toFixed(2)}
            />
          </HStack>
          <HStack align="stretch" spacing={3} mb={2}>
            <TooltipReadonlyField
              label="Mark up %"
              prefix="%"
              value={markupStr != null ? markupStr : '—'}
            />
            <TooltipReadonlyField
              label="Price offset %"
              prefix="%"
              value={tolStr != null ? tolStr : '—'}
            />
          </HStack>
          {sup.syncedDetails?.externalProvidedShippingCost != null &&
          !Number.isNaN(Number(sup.syncedDetails.externalProvidedShippingCost)) ? (
            <Text fontSize="sm" color={textColor} mb={2}>
              <Text as="span" color={mutedTextColor}>
                External shipping:{' '}
              </Text>
              ${Number(sup.syncedDetails.externalProvidedShippingCost).toFixed(2)}
            </Text>
          ) : null}
          {sup.syncedDetails?.appliedSellingPriceFormula ? (
            <Box mb={pricesObj ? 2 : 0}>
              <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} mb={1}>
                Applied selling price formula
              </Text>
              <Input
                value={sup.syncedDetails.appliedSellingPriceFormula}
                isReadOnly
                size="xs"
                fontFamily="mono"
                fontSize="xs"
                bg={tooltipCodeBg}
                color={textColor}
                borderColor={borderColor}
              />
            </Box>
          ) : null}
          {pricesObj ? (
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} mb={1}>
                Prices
              </Text>
              <Box
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="md"
                bg={tooltipCodeBg}
                p={2}
                maxH="120px"
                overflowY="auto"
                fontFamily="mono"
                fontSize="xs"
                color={textColor}
                whiteSpace="pre-wrap"
              >
                {JSON.stringify(pricesObj, null, 2)}
              </Box>
            </Box>
          ) : null}
        </>
      ) : (
        <Text fontSize="sm" color={mutedTextColor}>
          No supplier data on this listing.
        </Text>
      )}

      {opt &&
      (opt.baseFormulaPrice != null ||
        (opt.syncedStatus != null && String(opt.syncedStatus).trim() !== '')) ? (
        <>
          <Divider borderColor={borderColor} my={2.5} />
          <Text fontWeight="semibold" fontSize="sm" color={tooltipHeading} mb={1}>
            Optimization
          </Text>
          {opt.baseFormulaPrice != null && !Number.isNaN(Number(opt.baseFormulaPrice)) ? (
            <Text fontSize="xs" color={textColor} mb={0.5}>
              <Text as="span" color={mutedTextColor}>
                Base formula price:{' '}
              </Text>
              ${Number(opt.baseFormulaPrice).toFixed(4)}
            </Text>
          ) : null}
          {opt.syncedStatus ? (
            <Text fontSize="xs" color={textColor}>
              <Text as="span" color={mutedTextColor}>
                Sync status:{' '}
              </Text>
              {opt.syncedStatus}
            </Text>
          ) : null}
        </>
      ) : null}
    </VStack>
  );
};

const ListingQtyTooltipLabel = ({ lr }) => {
  const { textColor, mutedTextColor, borderColor, tooltipHeading, tooltipCodeBg } = useColors();
  const sup = lr.supplier;
  const cfg = lr.storeSupplierConfig;

  return (
    <VStack align="stretch" spacing={0} maxW="380px">
      <Text fontWeight="semibold" fontSize="sm" color={tooltipHeading} mb={1}>
        Quantity &amp; stock
      </Text>
      <HStack align="stretch" spacing={3} mb={2}>
        <TooltipReadonlyField label="Listing quantity" prefix="#" value={String(lr.quantity ?? '—')} />
        {sup ? (
          <TooltipReadonlyField label="Available quantity" prefix="#" value={String(sup.stock ?? '—')} />
        ) : (
          <TooltipReadonlyField label="Available quantity" prefix="#" value="—" />
        )}
      </HStack>
      {sup ? (
        <Text fontSize="xs" color={textColor} mb={2}>
          <Text as="span" color={mutedTextColor}>
            Supplier sync qty:{' '}
          </Text>
          {sup.quantity}
        </Text>
      ) : (
        <Text fontSize="sm" color={mutedTextColor} mb={2}>
          No supplier data on this listing.
        </Text>
      )}

      {cfg ? (
        <>
          <Divider borderColor={borderColor} my={2} />
          <Text fontWeight="semibold" fontSize="sm" color={tooltipHeading} mb={2}>
            Quantity settings
          </Text>
          <HStack align="stretch" spacing={3} mb={2}>
            <TooltipReadonlyField
              label="Quantity offset"
              prefix="#"
              value={cfg.quantityOffset != null ? String(cfg.quantityOffset) : '—'}
            />
            <FormControl size="xs">
              <FormLabel fontSize="xs" fontWeight="semibold" color={mutedTextColor} mb={1}>
                Default max qty limit
              </FormLabel>
              {cfg.defaultQuantityMaxLimitEnabled ? (
                <InputGroup size="xs">
                  <InputLeftAddon
                    px={2}
                    fontSize="xs"
                    bg={tooltipCodeBg}
                    borderColor={borderColor}
                    children="#"
                  />
                  <Input
                    value={cfg.quantityMaxLimit != null ? String(cfg.quantityMaxLimit) : '—'}
                    isReadOnly
                    size="xs"
                    fontSize="xs"
                    bg={tooltipCodeBg}
                    color={textColor}
                    borderColor={borderColor}
                  />
                </InputGroup>
              ) : (
                <Text fontSize="xs" color={mutedTextColor} fontStyle="italic" pt={1}>
                  Disabled
                </Text>
              )}
            </FormControl>
          </HStack>
          <HStack spacing={2} mb={cfg.priceRangeMatch ? 2 : 0}>
            <Text fontSize="xs" color={mutedTextColor}>
              Stock availability fallback:
            </Text>
            <Badge fontSize="xs" colorScheme={cfg.stockAvailabilityFallback ? 'green' : 'gray'} variant="subtle">
              {cfg.stockAvailabilityFallback ? 'Active' : 'Inactive'}
            </Badge>
          </HStack>
          {cfg.priceRangeMatch ? (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color={tooltipHeading} mb={2}>
                Price-range max quantity
              </Text>
              <HStack align="stretch" spacing={2}>
                <TooltipReadonlyField
                  label="Min price"
                  prefix="$"
                  value={cfg.priceRangeMatch.minPrice.toFixed(2)}
                />
                <TooltipReadonlyField
                  label="Max price"
                  prefix="$"
                  value={cfg.priceRangeMatch.maxPrice.toFixed(2)}
                />
                <TooltipReadonlyField
                  label="Allowed qty"
                  prefix="#"
                  value={String(cfg.priceRangeMatch.allowedQuantity)}
                />
              </HStack>
            </Box>
          ) : null}
        </>
      ) : null}
    </VStack>
  );
};

const ItemsTable = ({ refreshKey = 0, onExportQueryChange }) => {
  const dispatch = useDispatch();
  const {
    textColor,
    mutedTextColor,
    borderColor,
    headerBg,
    hoverBg,
    cardBg,
    inputColor,
    placeholderColor,
    tooltipBg,
    tooltipBorder,
    tooltipShadow,
  } = useColors();

  const overview = useSelector((s) => s.skuManagement.skuItemsOverview);
  const pageData = useSelector((s) => s.skuManagement.skuItemsPage);
  const getOverviewStatus = useSelector((s) => s.skuManagement.getSkuItemsOverviewStatus);
  const getPageStatus = useSelector((s) => s.skuManagement.getSkuItemsPageStatus);
  const itemsError = useSelector((s) => s.skuManagement.itemsPageError);

  const storesList = useSelector((s) => s.stores.list);
  const suppliersList = useSelector((s) => s.suppliers.list);
  const getStoresListStatus = useSelector((s) => s.stores.getStoresListStatus);
  const getSuppliersListStatus = useSelector((s) => s.suppliers.getSuppliersListStatus);

  const [search, setSearch] = useState('');
  const [health, setHealth] = useState('all');
  const [dataProfileFilter, setDataProfileFilter] = useState('all');
  const [storeId, setStoreId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [pageInput, setPageInput] = useState(1);
  const [appliedQuery, setAppliedQuery] = useState({});
  const [expandedSection, setExpandedSection] = useState({ itemId: null, section: null });
  const [attributeModal, setAttributeModal] = useState(null);
  const [copyFromListingRow, setCopyFromListingRow] = useState(null);
  const [resyncingSkuId, setResyncingSkuId] = useState(null);
  const [deleteListingTarget, setDeleteListingTarget] = useState(null);
  const toast = useToast();

  const reloadPage = useCallback(() => {
    dispatch(
      getSkuItemsPageApi({
        page,
        limit,
        ...appliedQuery,
      }),
    );
    dispatch(getSkuItemsOverviewApi());
  }, [dispatch, page, limit, appliedQuery]);

  const items = pageData?.items || [];
  const totalPages = pageData?.totalPages || 0;
  const totalCount = pageData?.totalCount ?? 0;

  useEffect(() => {
    dispatch(getStoresListApi());
    dispatch(getSuppliersListApi());
    dispatch(getSkuItemsOverviewApi());
  }, [dispatch]);

  useEffect(() => {
    dispatch(clearSkuManagementError());
    dispatch(
      getSkuItemsPageApi({
        page,
        limit,
        ...appliedQuery,
      }),
    );
  }, [dispatch, page, limit, appliedQuery]);

  useEffect(() => {
    onExportQueryChange?.({ ...appliedQuery });
  }, [appliedQuery, onExportQueryChange]);

  useEffect(() => {
    if (refreshKey > 0) {
      reloadPage();
    }
  }, [refreshKey, reloadPage]);

  useEffect(() => {
    setAppliedQuery((prev) => {
      const next = {
        searchField: 'all',
        ...(prev.search ? { search: prev.search } : {}),
        ...(health !== 'all' ? { health } : {}),
        ...(storeId ? { storeId } : {}),
        ...(supplierId ? { supplierId } : {}),
        ...(dataProfileFilter !== 'all' ? { dataProfile: dataProfileFilter } : {}),
      };
      const same =
        (prev.search || undefined) === (next.search || undefined) &&
        (prev.health || undefined) === (next.health || undefined) &&
        (prev.storeId || '') === (next.storeId || '') &&
        (prev.supplierId || '') === (next.supplierId || '') &&
        (prev.searchField || 'all') === (next.searchField || 'all') &&
        (prev.dataProfile || 'all') === (next.dataProfile || 'all');
      if (same) {
        return prev;
      }
      return next;
    });
    setPage(1);
  }, [health, storeId, supplierId, dataProfileFilter]);

  useEffect(() => {
    setPageInput(page);
  }, [page]);

  const handleSearchSubmit = () => {
    setAppliedQuery((prev) => {
      const next = {
        ...prev,
        search: search.trim() || undefined,
        searchField: 'all',
      };
      if (!next.search) {
        delete next.search;
      }
      return next;
    });
    setPage(1);
  };

  const handlePageChange = (p) => {
    setPage(p);
  };

  const toggleSection = (itemId, section) => {
    setExpandedSection((prev) =>
      prev.itemId === itemId && prev.section === section
        ? { itemId: null, section: null }
        : { itemId, section },
    );
  };

  const storeOptions = useMemo(
    () =>
      (Array.isArray(storesList) ? storesList : []).filter((s) => s?.id || s?.value),
    [storesList],
  );
  const supplierOptions = useMemo(
    () =>
      (Array.isArray(suppliersList) ? suppliersList : []).filter((s) => s?.id || s?.value),
    [suppliersList],
  );

  const isLoadingPage = getPageStatus === 'pending';

  const refreshItemsPage = useCallback(() => {
    dispatch(clearSkuManagementError());
    dispatch(
      getSkuItemsPageApi({
        page,
        limit,
        ...appliedQuery,
      }),
    );
  }, [dispatch, page, limit, appliedQuery]);

  const handleResyncListings = async (row) => {
    if (!row?.id || resyncingSkuId) {
      return;
    }
    setResyncingSkuId(row.id);
    try {
      const data = await dispatch(resyncSkuItemListingsApi({ skuMongoId: row.id })).unwrap();
      const results = data?.listingResults || [];
      const failed = results.filter((r) => !r.success).length;
      toast({
        title: data?.message || 'Listings resynced',
        description:
          results.length === 0
            ? 'This SKU has no linked listings to update.'
            : failed > 0
              ? `${results.length - failed} of ${results.length} eBay listing(s) updated from catalog.`
              : `Updated ${results.length} linked eBay listing(s) from current catalog data.`,
        status: results.length === 0 ? 'info' : failed > 0 ? 'warning' : 'success',
        duration: 6000,
      });
    } catch (e) {
      toast({
        title: 'Resync failed',
        description:
          (typeof e === 'string' && e) ||
          e?.payload?.message ||
          e?.message ||
          'Could not resync listings from catalog',
        status: 'error',
      });
    } finally {
      setResyncingSkuId(null);
    }
  };

  return (
    <VStack align="stretch" spacing={8}>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={5}>
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
          <Stat>
            <HStack>
              <Icon as={MdInventory2} boxSize={8} color="blue.400" />
        <Box>
                <StatLabel>Total SKUs</StatLabel>
                <StatNumber>
                  {getOverviewStatus === 'pending' ? '—' : overview?.totalSkus ?? 0}
                </StatNumber>
                <StatHelpText>Compiled at item level</StatHelpText>
        </Box>
        </HStack>
          </Stat>
        </Box>
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
          <Stat>
            <HStack>
              <Icon as={MdStorefront} boxSize={8} color="purple.400" />
              <Box>
                <StatLabel>Listings linked</StatLabel>
                <StatNumber>
                  {getOverviewStatus === 'pending' ? '—' : overview?.linkedListingsCount ?? 0}
                </StatNumber>
                <StatHelpText>Total listing links on SKU rows</StatHelpText>
              </Box>
            </HStack>
          </Stat>
        </Box>
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
          <Stat>
            <HStack>
              <Icon as={MdLinkOff} boxSize={8} color="orange.400" />
              <Box>
                <StatLabel>Listings unlinked</StatLabel>
                <StatNumber>
                  {getOverviewStatus === 'pending' ? '—' : overview?.skusUnlinked ?? 0}
                </StatNumber>
                <StatHelpText>SKUs with no listing links</StatHelpText>
              </Box>
            </HStack>
          </Stat>
        </Box>
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
          <Stat>
            <HStack>
              <Icon as={MdStorefront} boxSize={8} color="green.400" />
              <Box>
                <StatLabel>Active stores</StatLabel>
                <StatNumber>
                  {getOverviewStatus === 'pending' ? '—' : overview?.distinctListingStores ?? 0}
                </StatNumber>
                <StatHelpText>Distinct stores on linked SKUs</StatHelpText>
              </Box>
            </HStack>
          </Stat>
        </Box>
      </Grid>

      <Box>
        <Text fontSize="lg" fontWeight="semibold" color={textColor} mb={2}>
          Items
        </Text>
        <Text fontSize="sm" color={mutedTextColor} mb={5}>
          SKU-level catalog overview across listings, stores, suppliers, and item attributes.
        </Text>

        <VStack align="stretch" spacing={5}>
          <Flex gap={4} flexWrap="wrap" align="flex-end">
            <Box flex="1" minW={{ base: '100%', md: '320px', lg: '420px' }} maxW="720px">
              <InputGroup size="md">
                <InputLeftAddon bg={headerBg}>
                  <SearchIcon color="gray.500" />
                </InputLeftAddon>
                <Input
                  color={inputColor}
                  placeholder="Search SKU or title"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                  _placeholder={{ color: placeholderColor }}
                />
              </InputGroup>
            </Box>
            <Box minW="160px">
              <Text fontSize="xs" mb={1} color={mutedTextColor}>
                Store
              </Text>
              <Select
                size="md"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                color={inputColor}
                isDisabled={getStoresListStatus === 'pending'}
              >
                <option value="">All stores</option>
                {storeOptions.map((s) => (
                  <option key={s.id || s.value} value={s.value || s.id}>
                    {s.label || s.name}
                  </option>
                ))}
              </Select>
            </Box>
            <Box minW="160px">
              <Text fontSize="xs" mb={1} color={mutedTextColor}>
                Supplier
              </Text>
              <Select
                size="md"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                color={inputColor}
                isDisabled={getSuppliersListStatus === 'pending'}
              >
                <option value="">All suppliers</option>
                {supplierOptions.map((s) => (
                  <option key={s.id || s.value} value={s.value || s.id}>
                    {s.label || s.name}
                  </option>
                ))}
              </Select>
            </Box>
            <Box minW="180px">
              <Text fontSize="xs" mb={1} color={mutedTextColor}>
                Health
              </Text>
              <Select
                size="md"
                value={health}
                onChange={(e) => setHealth(e.target.value)}
                color={inputColor}
              >
                <option value="all">All health</option>
                <option value="linked">Linked listings</option>
                <option value="unlinked">Unlinked</option>
              </Select>
            </Box>
            <Box minW="180px">
              <Text fontSize="xs" mb={1} color={mutedTextColor}>
                Data profile
              </Text>
              <Select
                size="md"
                value={dataProfileFilter}
                onChange={(e) => setDataProfileFilter(e.target.value)}
                color={inputColor}
              >
                <option value="all">All profiles</option>
                <option value="complete">Complete</option>
                <option value="partial">Partial</option>
                <option value="incomplete">Incomplete</option>
                <option value="notcalculated">Not calculated</option>
              </Select>
            </Box>
          </Flex>

          {itemsError ? (
            <Text color="red.500" fontSize="sm">
              {itemsError}
            </Text>
          ) : null}

      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflow="hidden">
            {isLoadingPage && items.length === 0 ? (
              <Flex justify="center" py={14}>
                <Spinner />
              </Flex>
            ) : (
        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
                    <Th color={textColor} w="22%" py={4} px={4}>
                      SKU
                    </Th>
                    <Th color={textColor} w="16%" py={4} px={4}>
                      Store coverage
                    </Th>
                    <Th color={textColor} w="14%" py={4} px={4}>
                      Supplier
                    </Th>
                    <Th color={textColor} w="16%" py={4} px={4}>
                      Order activity
                    </Th>
                    <Th color={textColor} w="16%" py={4} px={4}>
                      Item attributes
                    </Th>
                    <Th color={textColor} w="16%" py={4} px={4}>
                      Data profile
                    </Th>
            </Tr>
          </Thead>
          <Tbody>
                  {items.map((row) => {
                    const attrs = row.attributes || {};
                    const pb = profileBadge(row.dataProfile);
                    const optimizerBadge = optimizerStatusBadge(row.optimizerTracking);
                    const fitmentVerified = row.optimizerTracking?.fitmentVerified === true;
                    const itemSpecificsVerified = row.optimizerTracking?.itemSpecificsVerified === true;
                    const listingRows = row.listingRows || [];
                    const names = row.supplierNames || [];
                    const supplierList = supplierRowsFromItem(row);
                    const primarySupplier = names[0] || supplierList[0]?.name || null;
                    const totalSupplierQty = sumSupplierInventoryQuantities(supplierList);
                    const avgListPriceEligible = avgListedPriceWhereSellingPriceExists(listingRows);
                    const isStoreOpen =
                      expandedSection.itemId === row.id && expandedSection.section === 'stores';
                    const isSuppliersOpen =
                      expandedSection.itemId === row.id && expandedSection.section === 'suppliers';
                    const isAttributesOpen =
                      expandedSection.itemId === row.id && expandedSection.section === 'attributes';
                    const isOrdersOpen =
                      expandedSection.itemId === row.id && expandedSection.section === 'orders';
                    const orderStats = row.orderStats || emptyOrderStats();

              return (
                      <React.Fragment key={row.id}>
                        <Tr _hover={{ bg: hoverBg }}>
                          <Td verticalAlign="top" py={5} px={4} maxW="320px">
                            <HStack align="start" spacing={3}>
                              <Image
                                src={row.previewImageUrl}
                                alt={row.displayTitle || row.canonicalSku}
                                boxSize="72px"
                                objectFit="contain"
                                borderRadius="md"
                                flexShrink={0}
                                fallback={<Box boxSize="72px" bg="gray.200" borderRadius="md" />}
                              />
                              <VStack align="start" spacing={2} minW={0} flex={1}>
                              <Text fontWeight="semibold" color="blue.600" noOfLines={2}>
                                {row.canonicalSku}
                              </Text>
                              {row.isSetSku ? (
                                <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                                  Set SKU
                                </Badge>
                              ) : null}
                              <Text fontSize="sm" color={textColor} noOfLines={3}>
                                {row.displayTitle || '—'}
                              </Text>
                              <HStack spacing={3} flexWrap="wrap" pt={1}>
                                <Badge
                                  colorScheme="blue"
                                  variant="subtle"
                                  px={3}
                                  py={1}
                                  borderRadius="md"
                                  fontWeight="medium"
                                >
                                  <HStack as="span" spacing={2}>
                                    <Text as="span">{row.listingsCount}</Text>
                                    <Text as="span">listings</Text>
                                  </HStack>
                                </Badge>
                                <Badge
                                  colorScheme="gray"
                                  variant="subtle"
                                  px={3}
                                  py={1}
                                  borderRadius="md"
                                  fontWeight="medium"
                                >
                                  {totalSupplierQty != null ? `Qty ${totalSupplierQty}` : 'Qty —'}
                          </Badge>
                                <Tooltip
                                  hasArrow
                                  placement="top"
                                  openDelay={200}
                                  maxW="280px"
                                  bg={tooltipBg}
                                  color={textColor}
                                  borderWidth="1px"
                                  borderColor={tooltipBorder}
                                  boxShadow={tooltipShadow}
                                  borderRadius="md"
                                  px={3}
                                  py={2}
                                  label="Average listed (marketplace) price on linked listings that also have a supplier selling price."
                                >
                                  <Badge
                                    colorScheme="purple"
                                    variant="subtle"
                                    px={3}
                                    py={1}
                                    borderRadius="md"
                                    fontWeight="medium"
                                    cursor="help"
                                  >
                                    {avgListPriceEligible != null
                                      ? `Avg price $${avgListPriceEligible.toFixed(2)}`
                                      : 'Avg price —'}
                          </Badge>
                                </Tooltip>
                        </HStack>
                      </VStack>
                            </HStack>
                    </Td>
                          <Td verticalAlign="top" py={5} px={4}>
                      <VStack align="start" spacing={2}>
                              <Text fontSize="sm" color={textColor} lineHeight="1.5">
                                {(row.storeNames || []).join(', ') || '—'}
                            </Text>
                        <Button
                          size="xs"
                          variant="outline"
                                mt={1}
                                rightIcon={isStoreOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                onClick={() => toggleSection(row.id, 'stores')}
                              >
                                {isStoreOpen ? 'Hide stores' : 'View stores'}
                        </Button>
                      </VStack>
                    </Td>
                          <Td verticalAlign="top" py={5} px={4}>
                      <VStack align="start" spacing={2}>
                              {primarySupplier ? (
                                <Text fontSize="sm" color={textColor} fontWeight="semibold">
                                  {primarySupplier}
                        </Text>
                              ) : (
                                <Text fontSize="sm" color={mutedTextColor}>
                                  No suppliers linked
                        </Text>
                              )}
                              {names.length > 1 ? (
                                <Text fontSize="xs" color={mutedTextColor} noOfLines={2}>
                                  {names.slice(1, 3).join(', ')}
                                  {names.length > 3 ? ` +${names.length - 3} more` : ''}
                                </Text>
                              ) : null}
                        <Button
                          size="xs"
                          variant="outline"
                                rightIcon={isSuppliersOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                onClick={() => toggleSection(row.id, 'suppliers')}
                              >
                                {isSuppliersOpen ? 'Hide suppliers' : 'View suppliers'}
                        </Button>
                      </VStack>
                    </Td>
                          <Td verticalAlign="top" py={5} px={4}>
                      <VStack align="start" spacing={2}>
                              <Text fontSize="sm" color={textColor} fontWeight="semibold">
                                {orderStats.orderCount > 0
                                  ? `${orderStats.orderCount.toLocaleString()} order${orderStats.orderCount === 1 ? '' : 's'}`
                                  : 'No orders'}
                              </Text>
                              <Text fontSize="xs" color={mutedTextColor}>
                                {orderStats.unitsOrdered > 0
                                  ? `${orderStats.unitsOrdered.toLocaleString()} unit${orderStats.unitsOrdered === 1 ? '' : 's'} sold`
                                  : '—'}
                              </Text>
                              <HStack spacing={3} flexWrap="wrap">
                                {orderStats.cancellationCount > 0 ? (
                                  <StatCountBadge
                                    colorScheme="orange"
                                    count={orderStats.cancellationCount}
                                    label="cancel"
                                  />
                                ) : null}
                                {orderStats.returnCount > 0 ? (
                                  <StatCountBadge
                                    colorScheme="red"
                                    count={orderStats.returnCount}
                                    label="return"
                                  />
                                ) : null}
                                {orderStats.partialRefundCount > 0 ? (
                                  <StatCountBadge
                                    colorScheme="yellow"
                                    count={orderStats.partialRefundCount}
                                    label="partial"
                                  />
                                ) : null}
                              </HStack>
                              {orderStats.topSupplierByOrders?.name ? (
                                <Text fontSize="xs" color={mutedTextColor} noOfLines={2}>
                                  Top source: {orderStats.topSupplierByOrders.name}
                                </Text>
                              ) : null}
                        <Button
                          size="xs"
                          variant="outline"
                                rightIcon={isOrdersOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                onClick={() => toggleSection(row.id, 'orders')}
                              >
                                {isOrdersOpen ? 'Hide order details' : 'View order details'}
                        </Button>
                      </VStack>
                    </Td>
                          <Td verticalAlign="top" py={5} px={4}>
                      <VStack align="start" spacing={2}>
                              {optimizerBadge ? (
                                <Tooltip
                                  label={optimizerBadge.tooltip || optimizerBadge.label}
                                  isDisabled={!optimizerBadge.tooltip}
                                  hasArrow
                                >
                          <Badge
                                    colorScheme={optimizerBadge.colorScheme}
                            variant="subtle"
                                    fontSize="xs"
                                    px={2}
                                    py={0.5}
                          >
                                    {optimizerBadge.label}
                          </Badge>
                                </Tooltip>
                              ) : null}
                              <VStack align="start" spacing={1}>
                                <Text fontSize="sm" color={textColor}>
                                  Images {attrs.imageCount ?? 0}
                                </Text>
                                <AttributeCountRow
                                  label="Specs"
                                  count={attrs.specsCount ?? 0}
                                  verified={itemSpecificsVerified}
                                  textColor={textColor}
                                />
                                <AttributeCountRow
                                  label="Fitment"
                                  count={attrs.fitmentCount ?? 0}
                                  verified={fitmentVerified}
                                  textColor={textColor}
                                />
                              </VStack>
                        <Button
                          size="xs"
                          variant="outline"
                                mt={1}
                                rightIcon={isAttributesOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                onClick={() => toggleSection(row.id, 'attributes')}
                              >
                                {isAttributesOpen ? 'Hide attributes' : 'View attributes'}
                        </Button>
                      </VStack>
                    </Td>
                          <Td verticalAlign="top" py={5} px={4}>
                      <VStack align="start" spacing={2}>
                              <HStack spacing={2} w="full" justify="space-between">
                                <Tooltip label={dataProfileTooltip(row)} hasArrow placement="top-start">
                                  <Badge colorScheme={pb.colorScheme} px={2.5} py={0.5} cursor="help">
                                    {pb.label}
                                  </Badge>
                                </Tooltip>
                                <SkuOptionsMenu
                                  row={row}
                                  onDeleted={reloadPage}
                                  onUpdated={reloadPage}
                                />
                              </HStack>
                        <Text fontSize="xs" color={mutedTextColor}>
                                Updated {fmtDate(row.updatedAt)}
                        </Text>
                              <Tooltip
                                label={
                                  (row.listingsCount ?? 0) > 0
                                    ? 'Push current catalog title, images, specs, and fitment to all linked eBay listings'
                                    : 'No linked listings to update'
                                }
                                hasArrow
                                placement="top"
                              >
                                <Button
                                  size="xs"
                                  variant="outline"
                                  colorScheme="blue"
                                  isLoading={resyncingSkuId === row.id}
                                  isDisabled={
                                    resyncingSkuId != null ||
                                    (row.listingsCount ?? 0) < 1
                                  }
                                  onClick={() => handleResyncListings(row)}
                                >
                                  Resync data
                                </Button>
                              </Tooltip>
                      </VStack>
                    </Td>
                  </Tr>

                        {(isStoreOpen || isSuppliersOpen || isOrdersOpen || isAttributesOpen) && (
                          <Tr>
                            <Td colSpan={6} px={6} py={5} bg={headerBg} borderTopWidth="1px" borderColor={borderColor}>
                              {isStoreOpen && (
                                <VStack align="stretch" spacing={4}>
                                  <Text fontWeight="bold" color={textColor}>
                                    Store-level listing details
                            </Text>
                            <Box
                              borderWidth="1px"
                              borderColor={borderColor}
                              borderRadius="md"
                              bg={cardBg}
                              overflow="hidden"
                            >
                              <Grid
                                      templateColumns={STORE_GRID_COLS}
                                      gap={3}
                                      px={4}
                                      py={3}
                                borderBottomWidth="1px"
                                borderColor={borderColor}
                                bg={headerBg}
                              >
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                  Listing
                                </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                  Store
                                </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                  Price
                                </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Added date
                                </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} isTruncated>
                                        Avail qty
                                </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Stock
                                </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Sync
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Actions
                                </Text>
                              </Grid>
                                    {listingRows.map((lr, index) => (
                                  <Grid
                                        key={lr.listingId || index}
                                        templateColumns={STORE_GRID_COLS}
                                        gap={3}
                                        px={4}
                                        py={3}
                                        borderBottomWidth={index === listingRows.length - 1 ? '0' : '1px'}
                                    borderColor={borderColor}
                                        alignItems="start"
                                      >
                                        <Box minW={0}>
                                          {(() => {
                                            const liveUrl = marketplaceListingUrl(
                                              lr.marketplaceName,
                                              lr.externalPlatformId,
                                            );
                                            return liveUrl ? (
                                        <Link
                                                href={liveUrl}
                                                isExternal
                                          fontSize="sm"
                                                color={textColor}
                                                noOfLines={2}
                                                _hover={{ color: 'blue.500' }}
                                              >
                                                {lr.title}
                                        </Link>
                                            ) : (
                                              <Text fontSize="sm" color={textColor} noOfLines={2}>
                                                {lr.title}
                                              </Text>
                                            );
                                          })()}
                                          <Text fontSize="xs" color={mutedTextColor} mt={1.5}>
                                            <Text as="span">SKU: </Text>
                                          <Link
                                            as={RouterLink}
                                              to={`/admin/listings/${lr.listingId}`}
                                              color="blue.500"
                                              fontWeight="medium"
                                              _hover={{ textDecoration: 'underline' }}
                                            >
                                              {lr.sku}
                                          </Link>
                                        </Text>
                                        </Box>
                                    <Text fontSize="sm" color={textColor}>
                                          {lr.storeName}
                                    </Text>
                                        <Tooltip
                                          hasArrow
                                      placement="top-start"
                                          openDelay={250}
                                          maxW="420px"
                                          bg={tooltipBg}
                                          color={textColor}
                                          borderWidth="1px"
                                          borderColor={tooltipBorder}
                                          boxShadow={tooltipShadow}
                                          borderRadius="md"
                                          px={4}
                                          py={3}
                                          gutter={10}
                                          label={<ListingPriceTooltipLabel lr={lr} />}
                                        >
                                          <Text
                                            as="span"
                                            fontSize="sm"
                                            color="blue.500"
                                            fontWeight="medium"
                                            cursor="help"
                                            borderBottomWidth="1px"
                                            borderBottomStyle="dotted"
                                            borderBottomColor="blue.400"
                                          >
                                            ${Number(lr.price || 0).toFixed(2)}
                                              </Text>
                                        </Tooltip>
                                        <Text fontSize="sm" color={textColor}>
                                          {fmtDate(lr.createdAt)}
                                              </Text>
                                        <Tooltip
                                          hasArrow
                                          placement="top-start"
                                          openDelay={250}
                                          maxW="400px"
                                          bg={tooltipBg}
                                          color={textColor}
                                                borderWidth="1px"
                                          borderColor={tooltipBorder}
                                          boxShadow={tooltipShadow}
                                                borderRadius="md"
                                          px={4}
                                          py={3}
                                          gutter={10}
                                          label={<ListingQtyTooltipLabel lr={lr} />}
                                        >
                                          <Text
                                            as="span"
                                            fontSize="sm"
                                            color="blue.500"
                                            fontWeight="medium"
                                            cursor="help"
                                            borderBottomWidth="1px"
                                            borderBottomStyle="dotted"
                                            borderBottomColor="blue.400"
                                          >
                                            {lr.quantity}
                                                </Text>
                                        </Tooltip>
                                              <Box>
                                          <Badge
                                            colorScheme={isInStockLabel(lr.stockLabel) ? 'green' : 'red'}
                                            variant="subtle"
                                          >
                                            {lr.stockLabel}
                                          </Badge>
                                              </Box>
                                        <Box minW={0}>
                                          {marketplaceSyncLabel(lr.marketplaceSync) ? (
                                            <Tooltip
                                              hasArrow
                                      placement="top-start"
                                              openDelay={200}
                                              maxW="360px"
                                              label={
                                                lr.marketplaceSync?.message ||
                                                (lr.marketplaceSync?.at
                                                  ? `Last sync: ${fmtDate(lr.marketplaceSync.at)}`
                                                  : '')
                                              }
                                              isDisabled={
                                                !lr.marketplaceSync?.message && !lr.marketplaceSync?.at
                                              }
                                            >
                                                <Badge
                                                colorScheme={marketplaceSyncColorScheme(lr.marketplaceSync)}
                                                variant="subtle"
                                                fontSize="xs"
                                                cursor={
                                                  lr.marketplaceSync?.message ? 'help' : 'default'
                                                }
                                              >
                                                {marketplaceSyncLabel(lr.marketplaceSync)}
                                                </Badge>
                                            </Tooltip>
                                          ) : (
                                            <Text fontSize="xs" color={mutedTextColor}>
                                              —
                                            </Text>
                                          )}
                                              </Box>
                                              <Box>
                                          <IconButton
                                            aria-label="Delete listing"
                                            icon={<DeleteIcon />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            onClick={() =>
                                              setDeleteListingTarget({
                                                skuMongoId: row.id,
                                                listing: lr,
                                              })
                                            }
                                          />
                                              </Box>
                                            </Grid>
                                    ))}
                                  </Box>
                                </VStack>
                              )}

                              {isSuppliersOpen && (
                                <VStack align="stretch" spacing={4}>
                                  <Text fontWeight="bold" color={textColor}>
                                    Supplier sources
                                                </Text>
                                  <Box
                                                  borderWidth="1px"
                                                  borderColor={borderColor}
                                                  borderRadius="md"
                                    bg={cardBg}
                                    overflowX="auto"
                                  >
                                    <Grid
                                      templateColumns={SUPPLIER_SOURCES_GRID_COLS}
                                      gap={3}
                                      px={4}
                                      py={3}
                                      borderBottomWidth="1px"
                                                  borderColor={borderColor}
                                      bg={headerBg}
                                      minW="780px"
                                    >
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Supplier
                                                </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Source SKU
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        supplierIdentifier
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        oem
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        partslink
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        quantity
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Prices
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Shipping
                                      </Text>
                                            </Grid>
                                    {supplierList.length === 0 ? (
                                      <Box px={4} py={6}>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                          No supplier rows for this SKU.
                                              </Text>
                                      </Box>
                                    ) : (
                                      supplierList.map((s, idx) => {
                                        const inv = s.inventoryMatch;
                                        const setParts = Array.isArray(s.setPartRows)
                                          ? s.setPartRows
                                          : Array.isArray(inv?.setParts)
                                            ? inv.setParts
                                            : null;
                                        const prices = inv?.prices;
                                        const priceKeys =
                                          prices && typeof prices === 'object' && !Array.isArray(prices)
                                            ? Object.keys(prices).length
                                            : 0;
                                        return (
                                              <Grid
                                            key={s.supplierId || `${s.name}-${idx}`}
                                            templateColumns={SUPPLIER_SOURCES_GRID_COLS}
                                                gap={3}
                                            px={4}
                                            py={3}
                                            borderBottomWidth={idx === supplierList.length - 1 ? '0' : '1px'}
                                                    borderColor={borderColor}
                                            minW="780px"
                                          >
                                            <Text fontSize="sm" color={textColor} fontWeight="medium">
                                              {s.name || '—'}
                                                  </Text>
                                            <Text fontSize="sm" color={textColor} isTruncated>
                                              {dashCell(s.supplierIdentifier)}
                                                  </Text>
                                            <Text fontSize="sm" color={textColor} isTruncated>
                                              {setParts
                                                ? setParts
                                                    .map((p) => p.supplierIdentifier)
                                                    .filter(Boolean)
                                                    .join(' ')
                                                : dashCell(inv?.supplierIdentifier)}
                                                  </Text>
                                            <Text fontSize="sm" color={textColor} isTruncated>
                                              {dashCell(inv?.oem ?? s.oem)}
                                                  </Text>
                                            <Text fontSize="sm" color={textColor} isTruncated>
                                              {dashCell(inv?.partslink ?? s.partslink)}
                                                  </Text>
                                            <Text fontSize="sm" color={textColor}>
                                              {setParts ? (
                                                <VStack align="start" spacing={0}>
                                                  {setParts.map((p, pi) => (
                                                    <Text key={pi} fontSize="xs">
                                                      {p.subWarehouse ? `${p.subWarehouse}: ` : ''}
                                                      {typeof p.quantity === 'number' ? p.quantity : '—'}
                                              </Text>
                                                  ))}
                                                  {inv && typeof inv.quantity === 'number' ? (
                                                    <Text fontSize="xs" fontWeight="semibold">
                                                      Set qty (min): {inv.quantity}
                                                    </Text>
                                                  ) : null}
                                                </VStack>
                                              ) : inv &&
                                                typeof inv.quantity === 'number' &&
                                                !Number.isNaN(inv.quantity) ? (
                                                inv.quantity
                                              ) : (
                                                '—'
                                              )}
                                            </Text>
                                            <Box minW={0}>
                                              {priceKeys > 0 ? (
                                                <Tooltip
                                                  hasArrow
                                                  placement="top-start"
                                                  openDelay={200}
                                                  maxW="320px"
                                                  bg={tooltipBg}
                                                  color={textColor}
                                                borderWidth="1px"
                                                  borderColor={tooltipBorder}
                                                  boxShadow={tooltipShadow}
                                                borderRadius="md"
                                                px={3}
                                                py={2}
                                                  gutter={8}
                                                  label={<SupplierInventoryPricesTooltip prices={prices} />}
                                                >
                                                  <Text
                                                    as="span"
                                                fontSize="sm"
                                                    color="blue.500"
                                                    fontWeight="medium"
                                                    cursor="help"
                                                    borderBottomWidth="1px"
                                                    borderBottomStyle="dotted"
                                                    borderBottomColor="blue.400"
                                                  >
                                                    {priceKeys} field{priceKeys === 1 ? '' : 's'}
                                              </Text>
                                                </Tooltip>
                                              ) : (
                                                <Text fontSize="sm" color={mutedTextColor}>
                                                  —
                                                </Text>
                                              )}
                                            </Box>
                                            <EditableSupplierShippingCell
                                              displayValue={formatInventoryShipping(
                                                prices,
                                                s.inventoryIncludesShipping !== false,
                                                s.externalShippingCost,
                                              )}
                                              supplierId={s.supplierId}
                                              supplierName={s.name}
                                              sku={s.supplierIdentifier}
                                              initialCost={s.externalShippingCost}
                                              inventoryIncludesShipping={
                                                s.inventoryIncludesShipping !== false
                                              }
                                              externalShippingCost={s.externalShippingCost}
                                              onSaved={refreshItemsPage}
                                              textColor={textColor}
                                              mutedTextColor={mutedTextColor}
                                              borderColor={borderColor}
                                              tooltipBg={tooltipBg}
                                              tooltipBorder={tooltipBorder}
                                              tooltipShadow={tooltipShadow}
                                            />
                                  </Grid>
                                );
                                      })
                                    )}
                            </Box>
                          </VStack>
                        )}

                              {isOrdersOpen && (
                                <VStack align="stretch" spacing={4}>
                                  <Text fontWeight="bold" color={textColor}>
                                    Order activity details
                                  </Text>
                                  <HStack spacing={3} flexWrap="wrap">
                                    <StatCountBadge
                                      colorScheme="blue"
                                      count={orderStats.orderCount}
                                      label="orders"
                                    />
                                    <StatCountBadge
                                      colorScheme="gray"
                                      count={orderStats.unitsOrdered}
                                      label="units"
                                    />
                                    {orderStats.cancellationCount > 0 ? (
                                      <StatCountBadge
                                        colorScheme="orange"
                                        count={orderStats.cancellationCount}
                                        label="cancellations"
                                      />
                                    ) : null}
                                    {orderStats.returnCount > 0 ? (
                                      <StatCountBadge
                                        colorScheme="red"
                                        count={orderStats.returnCount}
                                        label="returns"
                                      />
                                    ) : null}
                                    {orderStats.partialRefundCount > 0 ? (
                                      <StatCountBadge
                                        colorScheme="yellow"
                                        count={orderStats.partialRefundCount}
                                        label="partial refunds"
                                      />
                                    ) : null}
                                  </HStack>
                                  {orderStats.topSupplierByOrders || orderStats.topReturnSupplier ? (
                                    <HStack spacing={6} flexWrap="wrap">
                                      {orderStats.topSupplierByOrders ? (
                                        <Text fontSize="sm" color={textColor}>
                                          Most ordered from:{' '}
                                          <Text as="span" fontWeight="semibold">
                                            {orderStats.topSupplierByOrders.name}
                                          </Text>
                                        </Text>
                                      ) : null}
                                      {orderStats.topReturnSupplier ? (
                                        <Text fontSize="sm" color={textColor}>
                                          Most returns from:{' '}
                                          <Text as="span" fontWeight="semibold">
                                            {orderStats.topReturnSupplier.name}
                                          </Text>
                                        </Text>
                                      ) : null}
                                    </HStack>
                                  ) : null}

                                  <Box
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                    borderRadius="md"
                                    bg={cardBg}
                                    overflowX="auto"
                                  >
                                    <Text fontSize="sm" fontWeight="semibold" color={textColor} px={4} py={3}>
                                      By supplier (SKU prefix or store listing)
                                    </Text>
                                    <Grid
                                      templateColumns={ORDER_SUPPLIER_GRID_COLS}
                                      gap={3}
                                      px={4}
                                      py={3}
                                      borderTopWidth="1px"
                                      borderColor={borderColor}
                                      bg={headerBg}
                                      minW="920px"
                                    >
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Supplier
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Orders
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Units
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Cancellations
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Cancel units
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Returns
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Return units
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Partial refunds
                                      </Text>
                                    </Grid>
                                    {(orderStats.supplierBreakdown || []).length === 0 ? (
                                      <Box px={4} py={6}>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                          No order line items matched this SKU yet.
                                        </Text>
                                      </Box>
                                    ) : (
                                      orderStats.supplierBreakdown.map((s, idx) => (
                                        <Grid
                                          key={s.supplierId || `${s.name}-${idx}`}
                                          templateColumns={ORDER_SUPPLIER_GRID_COLS}
                                          gap={3}
                                          px={4}
                                          py={3}
                                          borderTopWidth="1px"
                                          borderColor={borderColor}
                                          minW="920px"
                                        >
                                          <Text fontSize="sm" color={textColor} fontWeight="medium">
                                            {s.name || '—'}
                                          </Text>
                                          <Text fontSize="sm" color={textColor}>{s.orderCount}</Text>
                                          <Text fontSize="sm" color={textColor}>{s.unitsOrdered}</Text>
                                          <Text fontSize="sm" color={textColor}>{s.cancellationCount}</Text>
                                          <Text fontSize="sm" color={textColor}>{s.unitsCancelled}</Text>
                                          <Text fontSize="sm" color={textColor}>{s.returnCount}</Text>
                                          <Text fontSize="sm" color={textColor}>{s.unitsReturned}</Text>
                                          <Text fontSize="sm" color={textColor}>{s.partialRefundCount}</Text>
                                        </Grid>
                                      ))
                                    )}
                                  </Box>

                                  {(orderStats.marketplaceBreakdown || []).length > 0 ? (
                                    <Box
                                      borderWidth="1px"
                                      borderColor={borderColor}
                                      borderRadius="md"
                                      bg={cardBg}
                                      px={4}
                                      py={3}
                                    >
                                      <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={2}>
                                        By marketplace
                                      </Text>
                                      <HStack spacing={3} flexWrap="wrap">
                                        {orderStats.marketplaceBreakdown.map((m) => (
                                          <Badge
                                            key={m.marketplace}
                                            colorScheme="purple"
                                            variant="subtle"
                                            px={3}
                                            py={1}
                                            borderRadius="md"
                                            fontWeight="medium"
                                            textTransform="none"
                                          >
                                            <HStack as="span" spacing={2} flexWrap="wrap">
                                              <Text as="span" fontWeight="semibold">
                                                {(m.marketplace || 'unknown').toUpperCase()}
                                              </Text>
                                              <Text as="span">
                                                {m.orderCount} orders · {m.unitsOrdered} units
                                                {m.cancellationCount > 0 ? ` · ${m.cancellationCount} cancel` : ''}
                                                {m.returnCount > 0 ? ` · ${m.returnCount} return` : ''}
                                              </Text>
                                            </HStack>
                                          </Badge>
                                        ))}
                                      </HStack>
                                    </Box>
                                  ) : null}

                                  <Box
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                    borderRadius="md"
                                    bg={cardBg}
                                    overflowX="auto"
                                  >
                                    <Text fontSize="sm" fontWeight="semibold" color={textColor} px={4} py={3}>
                                      Recent order lines
                                    </Text>
                                    <Grid
                                      templateColumns={ORDER_RECENT_GRID_COLS}
                                      gap={3}
                                      px={4}
                                      py={3}
                                      borderTopWidth="1px"
                                      borderColor={borderColor}
                                      bg={headerBg}
                                      minW="980px"
                                    >
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Order ID
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Marketplace
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Store
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Supplier
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Qty
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Date
                                      </Text>
                                      <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor}>
                                        Status
                                      </Text>
                                    </Grid>
                                    {(orderStats.recentActivity || []).length === 0 ? (
                                      <Box px={4} py={6}>
                                        <Text fontSize="sm" color={mutedTextColor}>
                                          No recent order activity for this SKU.
                                        </Text>
                                      </Box>
                                    ) : (
                                      orderStats.recentActivity.map((activity, idx) => (
                                        <Grid
                                          key={`${activity.marketplaceOrderId}-${idx}`}
                                          templateColumns={ORDER_RECENT_GRID_COLS}
                                          gap={3}
                                          px={4}
                                          py={3}
                                          borderTopWidth="1px"
                                          borderColor={borderColor}
                                          minW="980px"
                                          alignItems="start"
                                        >
                                          <Text fontSize="sm" color="blue.600" fontWeight="medium" isTruncated>
                                            {activity.marketplaceOrderId}
                                          </Text>
                                          <Text fontSize="sm" color={textColor} textTransform="capitalize">
                                            {activity.marketplace || '—'}
                                          </Text>
                                          <Text fontSize="sm" color={textColor} isTruncated>
                                            {activity.storeName || '—'}
                                          </Text>
                                          <Text fontSize="sm" color={textColor} isTruncated>
                                            {activity.supplierName || '—'}
                                          </Text>
                                          <Text fontSize="sm" color={textColor}>
                                            {activity.quantity}
                                          </Text>
                                          <Text fontSize="sm" color={textColor}>
                                            {fmtDate(activity.orderDate)}
                                          </Text>
                                          <Box minW={0}>
                                            <OrderLineStatusBadges activity={activity} />
                                            {activity.returnReason ? (
                                              <Text fontSize="xs" color={mutedTextColor} mt={1} noOfLines={2}>
                                                {activity.returnReason}
                                              </Text>
                                            ) : null}
                                          </Box>
                                        </Grid>
                                      ))
                                    )}
                                  </Box>
                                </VStack>
                              )}

                              {isAttributesOpen && (
                          <VStack align="stretch" spacing={3}>
                                  <Text fontWeight="bold" color={textColor}>
                                    Item attributes
                            </Text>
                                  <Box
                              borderWidth="1px"
                              borderColor={borderColor}
                              borderRadius="md"
                                    p={5}
                              bg={cardBg}
                            >
                                    <Grid
                                      templateColumns={{
                                        base: '1fr',
                                        md: 'repeat(2, 1fr)',
                                        lg: 'repeat(3, 1fr)',
                                      }}
                                      gap={5}
                                    >
                                      <Box gridColumn={{ base: 'auto', lg: 'span 3' }}>
                                        <Text fontSize="xs" color={mutedTextColor} mb={1}>
                                          eBay primary category
                                      </Text>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium" noOfLines={2}>
                                          {attrs.primaryCategoryName || '—'}
                                      </Text>
                                        {attrs.primaryCategoryId ? (
                                          <Text fontSize="xs" color={mutedTextColor} mt={1}>
                                            ID {attrs.primaryCategoryId}
                            </Text>
                                        ) : null}
                                      </Box>
                                <Box>
                                  <Text fontSize="xs" color={mutedTextColor} mb={1}>
                                          Title
                                  </Text>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium" noOfLines={2}>
                                          {row.displayTitle || attrs.catalogTitle || '—'}
                                    </Text>
                                </Box>
                                <Box>
                                  <Text fontSize="xs" color={mutedTextColor} mb={1}>
                                          Images
                                  </Text>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium">
                                          {attrs.imageCount ?? 0} in catalog
                                    </Text>
                                      </Box>
                                      <Box>
                                        <HStack spacing={2} align="center" mb={1}>
                                          <Text fontSize="xs" color={mutedTextColor}>
                                            Specs
                                          </Text>
                                          {itemSpecificsVerified ? <OptimizerVerifiedBadge /> : null}
                                  </HStack>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium">
                                          {attrs.specsCount ?? 0} in catalog
                                        </Text>
                                </Box>
                                <Box>
                                        <HStack spacing={2} align="center" mb={1}>
                                          <Text fontSize="xs" color={mutedTextColor}>
                                    Fitment
                                  </Text>
                                          {fitmentVerified ? <OptimizerVerifiedBadge /> : null}
                                  </HStack>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium">
                                          {attrs.fitmentCount ?? 0} in catalog
                                        </Text>
                                </Box>
                              </Grid>
                                    <Divider my={4} borderColor={borderColor} />
                                    <Text fontSize="xs" color={mutedTextColor} mb={3}>
                                      Each editor saves the catalog chunk and pushes changes to linked eBay
                                      listings (ReviseItem). Fitment uses full replace on the marketplace.
                                    </Text>
                                    <HStack spacing={3} flexWrap="wrap">
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  variant="outline"
                                        onClick={() => setAttributeModal({ kind: 'title', row })}
                                >
                                        Edit title
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  variant="outline"
                                        onClick={() => setAttributeModal({ kind: 'images', row })}
                                >
                                        Edit images
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  variant="outline"
                                        onClick={() => setAttributeModal({ kind: 'specs', row })}
                                      >
                                        Edit specs
                                      </Button>
                                      <Button
                                        size="sm"
                                        colorScheme="blue"
                                        variant="outline"
                                        onClick={() => setAttributeModal({ kind: 'fitment', row })}
                                      >
                                        Edit fitment
                                      </Button>
                                      <Button
                                        size="sm"
                                        colorScheme="teal"
                                        variant="outline"
                                        onClick={() => setCopyFromListingRow(row)}
                                      >
                                        Copy from other listing
                                </Button>
                              </HStack>
                            </Box>
                          </VStack>
                        )}
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
            )}
      </Box>

          <ItemsPagination
            currentPage={page}
            totalPages={totalPages}
            pageInput={pageInput}
            setPageInput={setPageInput}
            handlePageChange={handlePageChange}
            isLoading={isLoadingPage}
            rows={items}
          />
                <Text fontSize="xs" color={mutedTextColor}>
            {totalCount} SKU row(s) total · page size {limit}
                </Text>
        </VStack>
              </Box>

      <ItemsCatalogAttributeModals
        modal={attributeModal}
        onClose={() => setAttributeModal(null)}
        onAfterSave={refreshItemsPage}
                        borderColor={borderColor}
        headerBg={headerBg}
        mutedTextColor={mutedTextColor}
        inputColor={inputColor}
      />

      <CopyFromListingModal
        isOpen={!!copyFromListingRow}
        row={copyFromListingRow}
        onClose={() => setCopyFromListingRow(null)}
        onAfterApply={refreshItemsPage}
                          borderColor={borderColor}
        cardBg={cardBg}
        mutedTextColor={mutedTextColor}
        textColor={textColor}
        inputColor={inputColor}
      />

      <DeleteListingModal
        isOpen={Boolean(deleteListingTarget)}
        onClose={() => setDeleteListingTarget(null)}
        skuMongoId={deleteListingTarget?.skuMongoId}
        listing={deleteListingTarget?.listing}
        onDeleted={reloadPage}
      />
    </VStack>
  );
};

export default ItemsTable;
